import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { settingsAccess } from '../../utils/settingsAccess';
import ViewOnlyNotice from '../../settings/ViewOnlyNotice';
import {
  Box,
  Typography,
  TextField,
  Button,
  ButtonGroup,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  InputAdornment,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Chip,
  Checkbox,
  FormControlLabel,
  TablePagination // Added for facility list pagination
} from '@mui/material';
import {
  Save as SaveIcon,
  Clear as ClearIcon,
  Search as SearchIcon,
  RestartAlt as ResetIcon,
  PlaylistAddCheck as ApplyDefaultsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Undo as UndoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useUser } from '../../context/UserContext';
import { useOrg } from '../../context/OrgContext';
import { debounce } from 'lodash';
import SignatureCanvas from 'react-signature-canvas';
import useRateCatalog from '../../hooks/useRateCatalog';
import CancelNegotiationButton from '../../components/RateAgreement/CancelNegotiationButton';

const DUTY_LEVELS = ['light', 'medium', 'heavy', 'additional', 'all'];
const DUTY_LEVEL_LABELS = {
  light: 'Light Duty',
  medium: 'Medium Duty',
  heavy: 'Heavy Duty',
  additional: 'Additional',
  all: 'All / N/A'
};

const getAuthHeader = () => {
  const token = localStorage.getItem('token');
  return { Authorization: `Bearer ${token}` };
};

function RateManagement({ readOnly = false }) {
  const { currentUser, isLoading: userLoading } = useUser();
  const { name: orgName } = useOrg();
  // The server's ordered catalog: the same rows, labels and order as the
  // Submissions Rates tab and the rate agreement page
  const rateCatalog = useRateCatalog();
  const SERVICE_CATEGORIES = { rates: rateCatalog.rates, additionalCharges: rateCatalog.additionalCharges };
  const ALL_SERVICE_TYPES = rateCatalog.catalog;
  const isMileageService = rateCatalog.isMileage;

  // Org 1 RM/OW/sOW change rates; every Org 1 role browses all facilities; others see their own facility
  const access = useMemo(() => settingsAccess(currentUser), [currentUser]);
  const canEditRates = !readOnly && access.sitesRates === 'write';
  const canBrowseAllFacilities = !!currentUser && !access.isOrg2;
  
  const [facilities, setFacilities] = useState([]);
  const [filteredFacilities, setFilteredFacilities] = useState([]);
  const [selectedFacilityId, setSelectedFacilityId] = useState('');
  const [selectedFacilityName, setSelectedFacilityName] = useState('');
  const [filters, setFilters] = useState({
    name: '',
    vendorNumber: '',
    city: '',
    state: '',
    county: '',
    zip: '',
    coveredZip: ''
  });
  const [loadingFacilities, setLoadingFacilities] = useState(false);
  const [loadingRates, setLoadingRates] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [systemDefaultRates, setSystemDefaultRates] = useState({});
  const [facilityRates, setFacilityRates] = useState({});
  const [rateEditState, setRateEditState] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [resetConfirmOpen, setResetConfirmOpen] = useState(false);
  const [applyConfirmOpen, setApplyConfirmOpen] = useState(false);
  const [generatePdfDialogOpen, setGeneratePdfDialogOpen] = useState(false);
  const [rateAgreementStatus, setRateAgreementStatus] = useState(null);
  const [rateAgreementToken, setRateAgreementToken] = useState(null);
  const [pendingAgreementId, setPendingAgreementId] = useState(null);
  const [loadingAgreementStatus, setLoadingAgreementStatus] = useState(false);

  const [currentRateAgreement, setCurrentRateAgreement] = useState(null);
  const [submittedRateAgreement, setSubmittedRateAgreement] = useState(null);
  
  const [proposedRates, setProposedRates] = useState([]);
  const [rateAgreementId, setRateAgreementId] = useState(null);
  const [loadingProposedRates, setLoadingProposedRates] = useState(false);
  
  // New state variables for local decision tracking
  const [rateDecisions, setRateDecisions] = useState({});
  const [allDecisionsComplete, setAllDecisionsComplete] = useState(false);
  const [submitDialogOpen, setSubmitDialogOpen] = useState(false);
  
  // Facility List Pagination State
  const [facilitiesPage, setFacilitiesPage] = useState(0);
  const [facilitiesRowsPerPage, setFacilitiesRowsPerPage] = useState(10);
  const [facilitiesTotalCount, setFacilitiesTotalCount] = useState(0);

  // Add new state variables
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [adminSignature, setAdminSignature] = useState({ name: '', signatureData: '' });
  const [selectedRejections, setSelectedRejections] = useState([]);
  const [rejectionDialogOpen, setRejectionDialogOpen] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');

  useEffect(() => {
    const fetchSystemDefaults = async () => {
      try {
        const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/rates/defaults`, { headers: getAuthHeader() });
        setSystemDefaultRates(response.data || {});
      } catch (err) {
        
        setSystemDefaultRates({});
      }
    };
    fetchSystemDefaults();
  }, []);

  const fetchFacilities = useCallback(debounce(async (currentFilters, page, rowsPerPage) => {
    if (!currentUser) {
      setFacilities([]);
      setFilteredFacilities([]);
      setFacilitiesTotalCount(0);
      return;
    }
    setLoadingFacilities(true);
    setError(null);
    try {
      const params = {
        ...currentFilters,
        excludeIncompleteApplications: 'true',
        page: page + 1, // API uses 1-based indexing
        limit: rowsPerPage,
      };

      if (!canBrowseAllFacilities && currentUser?.vendorNumber) {
        params.vendorNumber = currentUser.vendorNumber;
      }

      Object.keys(params).forEach(key => { if (!params[key] && key !== 'page') delete params[key]; }); // Keep page=1 even if 0
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/facilities`, { params, headers: getAuthHeader() });

      const responseData = response.data || { records: [], totalCount: 0 };
      setFacilities(responseData.records || []); // Keep original facilities if needed elsewhere?
      setFilteredFacilities(responseData.records || []); // This is used for the dropdown
      setFacilitiesTotalCount(responseData.totalCount || 0);

      // Auto-select logic might need adjustment if only one page exists but total > 1
      if (!canBrowseAllFacilities && responseData.records?.length === 1 && responseData.totalCount === 1) {
        setSelectedFacilityId(responseData.records[0]._id);
        setSelectedFacilityName(responseData.records[0].facilityName);
      }

      // Check if the currently selected facility is still in the fetched list for the current page
      if (selectedFacilityId && selectedFacilityId !== 'DEFAULT' && !responseData.records.some(f => f._id === selectedFacilityId)) {
        // If not found on the current page, maybe don't clear it immediately?
        // Or perhaps clear it only if the total count is low enough that it *should* be visible?
        // For now, keeping the original logic:
        // setSelectedFacilityId('');
        // setSelectedFacilityName('');
        // Consider if this behavior is desired with pagination.
      }
    } catch (err) {
      
      setError('Failed to fetch facilities.');
      setFacilities([]);
      setFilteredFacilities([]);
      setFacilitiesTotalCount(0);
    } finally {
      setLoadingFacilities(false);
    }
  }, 500), [canBrowseAllFacilities, currentUser]); // Removed selectedFacilityId dependency as it might cause issues with pagination updates

  const fetchRatesForSelection = useCallback(async () => {
    const isFetchingDefaults = selectedFacilityId === 'DEFAULT';
    const endpoint = isFetchingDefaults
      ? `${process.env.REACT_APP_API_URL}/api/rates/defaults`
      : `${process.env.REACT_APP_API_URL}/api/rates?facilityId=${selectedFacilityId}`;

    if (!selectedFacilityId) {
        setFacilityRates({});
        setRateEditState({});
        return;
    }

    setLoadingRates(true);
    setError(null);
    setRateEditState({});

    try {
      const response = await axios.get(endpoint, { headers: getAuthHeader() });
      const ratesData = response.data || {};
      let ratesResult = {};

      if (isFetchingDefaults) {
          ratesResult = ratesData;
          
      } else {
          ratesResult = ratesData;
           
      }
      setFacilityRates(ratesResult);
    } catch (err) {
      
      setError(`Failed to load rates for ${selectedFacilityName}.`);
      setFacilityRates({});
    } finally {
      setLoadingRates(false);
    }
  }, [selectedFacilityId, selectedFacilityName]);

  const fetchRateAgreementStatus = useCallback(async (facilityId) => {
    if (!facilityId || facilityId === 'DEFAULT') {
      setRateAgreementStatus(null);
      setRateAgreementToken(null);
      return;
    }
    setLoadingAgreementStatus(true);
    setError(null);
    try {
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/facilities/${facilityId}/agreement-status`,
        { headers: getAuthHeader() }
      );
      if (response.status === 200 && response.data) {
        setRateAgreementStatus(response.data.status || 'None');
        setRateAgreementToken(response.data.token || null);
        setPendingAgreementId(response.data.status === 'Pending' ? response.data.agreementId || null : null);
      } else {

         setRateAgreementStatus('Error');
         setRateAgreementToken(null);
         setPendingAgreementId(null);
      }
    } catch (err) {
      setPendingAgreementId(null);
      if (err.response?.status === 404) {

        setRateAgreementStatus('None');
        setRateAgreementToken(null);

      } else {

        setRateAgreementStatus('Error');
        setRateAgreementToken(null);
      }
    } finally {
      setLoadingAgreementStatus(false);
    }
  }, []);

  const fetchCurrentRateAgreement = useCallback(async (facilityId) => {
    if (!facilityId || facilityId === 'DEFAULT') {
      setCurrentRateAgreement(null);
      return;
    }

    try {
      // Always ask the server. The old shortcut reused rateAgreementToken
      // from React state when status was already 'Finalized' — right after
      // finalizing a NEW agreement that state still held the PREVIOUS
      // agreement's token (the refresh runs before setState lands), so the
      // View button opened the old document with the old signatures.
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/facilities/${facilityId}/rate-agreement/current`,
        { headers: getAuthHeader() }
      );

      if (response.status === 200 && response.data.rateAgreement) {
        setCurrentRateAgreement(response.data.rateAgreement);

      } else {
        setCurrentRateAgreement(null);
      }
    } catch (err) {
      if (err.response?.status !== 404) {

      }
      setCurrentRateAgreement(null);
    }
  }, []);
  
  const fetchProposedRates = useCallback(async (facilityId) => {
    if (!facilityId || facilityId === 'DEFAULT') return;
    
    setLoadingProposedRates(true);
    setError(null);
    
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/facilities/${facilityId}/proposed-rates`,
        { headers: getAuthHeader() }
      );
      
      if (response.data && response.data.proposedRates) {
        setProposedRates(response.data.proposedRates);
        if (response.data.rateAgreementId) {
          setRateAgreementId(response.data.rateAgreementId);
        }
      } else {
        setProposedRates([]);
      }
    } catch (err) {
      
      setError(err.response?.data?.message || 'Failed to fetch proposed rates');
      setProposedRates([]);
    } finally {
      setLoadingProposedRates(false);
    }
  }, []);
  
  // Handle accepting a rate (locally)
  const handleAcceptRate = (rateItem) => {
    
    const rateKey = `${rateItem.serviceType}|${rateItem.dutyLevel}`;
    
    // First update the decisions state
    setRateDecisions(prev => {
      const newDecisions = {
        ...prev,
        [rateKey]: 'accepted'
      };
      
      
      // Get all changed rates
      const changedRates = proposedRates.filter(rate => {
        if (rate.serviceType === 'goneOnArrival') {
          return Math.abs(rate.proposedRate - rate.originalRate) > 0.01;
        }
        return rate.proposedRate !== rate.originalRate;
      });
      
      // Get all changed rate keys
      const changedRateKeys = changedRates.map(rate =>
        `${rate.serviceType}|${rate.dutyLevel}`
      );
      
      // Check if all changed rates now have decisions
      const allKeysHaveDecisions = changedRateKeys.every(key =>
        newDecisions[key] !== undefined
      );
      
      // Update allDecisionsComplete state immediately
      setTimeout(() => {
        
        setAllDecisionsComplete(allKeysHaveDecisions);
      }, 0);
      
      return newDecisions;
    });
  };
  
  // Handle rejecting a rate (locally)
  const handleRejectRate = (rateItem) => {
    
    const rateKey = `${rateItem.serviceType}|${rateItem.dutyLevel}`;
    
    // First update the decisions state
    setRateDecisions(prev => {
      const newDecisions = {
        ...prev,
        [rateKey]: 'rejected'
      };
      
      
      // Get all changed rates
      const changedRates = proposedRates.filter(rate => {
        if (rate.serviceType === 'goneOnArrival') {
          return Math.abs(rate.proposedRate - rate.originalRate) > 0.01;
        }
        return rate.proposedRate !== rate.originalRate;
      });
      
      // Get all changed rate keys
      const changedRateKeys = changedRates.map(rate =>
        `${rate.serviceType}|${rate.dutyLevel}`
      );
      
      // Check if all changed rates now have decisions
      const allKeysHaveDecisions = changedRateKeys.every(key =>
        newDecisions[key] !== undefined
      );
      
      // Update allDecisionsComplete state immediately
      setTimeout(() => {
        
        setAllDecisionsComplete(allKeysHaveDecisions);
      }, 0);
      
      return newDecisions;
    });
  };
  
  // Handle undoing a decision
  const handleUndoDecision = (rateItem) => {
    
    const rateKey = `${rateItem.serviceType}|${rateItem.dutyLevel}`;
    
    // First set allDecisionsComplete to false immediately
    setAllDecisionsComplete(false);
    
    // Then update the decisions state
    setRateDecisions(prev => {
      const newDecisions = { ...prev };
      delete newDecisions[rateKey];
      
      
      // We need to call checkAllDecisionsComplete after the state is updated
      setTimeout(() => checkAllDecisionsComplete(), 0);
      
      return newDecisions;
    });
  };
  
  // Check if all changed rates have decisions
  const checkAllDecisionsComplete = () => {
    // Log for debugging
    
    
    
    // Force a more aggressive comparison for GOA rates
    const changedRates = proposedRates.filter(rate => {
      // For GOA rates (percentages), compare with a small tolerance
      if (rate.serviceType === 'goneOnArrival') {
        const diff = Math.abs(rate.proposedRate - rate.originalRate);
        
        return diff > 0.01;
      }
      // For regular rates, use direct comparison but log the values
      const isDifferent = rate.proposedRate !== rate.originalRate;
      
      return isDifferent;
    });
    
    
    
    
    if (changedRates.length === 0) {
      
      setAllDecisionsComplete(false);
      return;
    }
    
    // Get the keys of all changed rates
    const changedRateKeys = changedRates.map(rate => `${rate.serviceType}|${rate.dutyLevel}`);
    
    
    // Get the keys of all rates with decisions
    const decisionKeys = Object.keys(rateDecisions);
    
    
    // Check if all changed rates have decisions
    const allDecided = changedRateKeys.every(rateKey => {
      const hasDecision = decisionKeys.includes(rateKey);
      
      return hasDecision;
    });
    
    
    
    // Force update the state
    setAllDecisionsComplete(allDecided);
    
    // Add a timeout to check if the state was updated
    setTimeout(() => {
      
    }, 100);
  };
  
  // Open submit dialog
  const handleOpenSubmitDialog = () => {
    setSubmitDialogOpen(true);
  };
  
  // Submit all decisions to the backend
  const submitAllDecisions = async () => {
    if (!selectedFacilityId || !rateAgreementId) {
      
      setError("Missing required data to submit decisions");
      return;
    }
    
    setSubmitDialogOpen(false);
    setIsSaving(true);
    setError(null);
    
    try {
      // Get service provider email
      const facility = facilities.find(f => f._id === selectedFacilityId);
      const serviceProviderEmail = facility?.contactEmail;
      
      
      
      
      if (!serviceProviderEmail) {
        
        setError("No service provider email found. Please ensure the facility has a contact email.");
        setIsSaving(false);
        return;
      }
      
      // Prepare accepted and rejected rates
      const acceptedRates = [];
      const rejectedRates = [];
      
      Object.entries(rateDecisions).forEach(([rateKey, decision]) => {
        const [serviceType, dutyLevel] = rateKey.split('|');
        const rateInfo = { serviceType, dutyLevel };
        
        if (decision === 'accepted') {
          acceptedRates.push(rateInfo);
        } else {
          rejectedRates.push(rateInfo);
        }
      });
      
      
      
      
      // Check if all changed rates have been accepted and none have been rejected
      const changedRates = proposedRates.filter(rate => {
        if (rate.serviceType === 'goneOnArrival') {
          return Math.abs(rate.proposedRate - rate.originalRate) > 0.01;
        }
        return rate.proposedRate !== rate.originalRate;
      });
      
      const allChangedRateKeys = changedRates.map(rate => `${rate.serviceType}|${rate.dutyLevel}`);
      const allAccepted = allChangedRateKeys.every(key => rateDecisions[key] === 'accepted');
      const noneRejected = rejectedRates.length === 0;
      
      // If all changed rates are accepted and none are rejected, prompt for signature
      if (allAccepted && noneRejected) {
        
        setIsSaving(false);
        setSignatureDialogOpen(true);
        return;
      }
      
      // Create a simple signature for the representative
      const signatureData = currentUser?.name ?
        `data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==` :
        '';
      
      // Otherwise, proceed with regular submission
      const url = `${process.env.REACT_APP_API_URL}/api/facilities/${selectedFacilityId}/rate-agreement/${rateAgreementId}/submit-decisions`;
      
      
      const response = await axios.post(
        url,
        {
          acceptedRates,
          rejectedRates,
          serviceProviderEmail,
          signatureData: signatureData
        },
        { headers: getAuthHeader() }
      );
      
      
      
      if (response.data.emailSent) {
        setSuccess('Rate decisions submitted successfully. Email sent to service provider.');
      } else {
        setSuccess('Rate decisions submitted successfully, but there was an issue sending the email.');
        
      }
      
      // Reset local state
      setRateDecisions({});
      setAllDecisionsComplete(false);
      setProposedRates([]); // Clear proposed rates immediately
      
      // Refresh data
      fetchRateAgreementStatus(selectedFacilityId);
      fetchCurrentRateAgreement(selectedFacilityId);
      fetchSubmittedRateAgreement(selectedFacilityId);
      fetchRatesForSelection(); // Refresh facility rates
      
      // Add a small delay to ensure all state updates have been processed
      setTimeout(() => {
        
        // Force a refresh of the proposed rates section
        fetchProposedRates(selectedFacilityId);
      }, 1000);
    } catch (err) {
      
      
      setError(err.response?.data?.message || 'Failed to submit decisions');
    } finally {
      setIsSaving(false);
    }
  };

  const fetchSubmittedRateAgreement = useCallback(async (facilityId) => {
    if (!facilityId || facilityId === 'DEFAULT') {
      setSubmittedRateAgreement(null);
      return;
    }

    try {
      // Always ask the server (same stale-state shortcut removed here as in
      // fetchCurrentRateAgreement — reusing rateAgreementToken from state
      // could point the "In Negotiation" view at the wrong agreement).
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/facilities/${facilityId}/rate-agreement/submitted`,
        { headers: getAuthHeader() }
      );

      if (response.status === 200 && response.data.rateAgreement) {
        setSubmittedRateAgreement(response.data.rateAgreement);

      } else {
        setSubmittedRateAgreement(null);
      }
    } catch (err) {
      if (err.response?.status !== 404) {

      }
      setSubmittedRateAgreement(null);
    }
  }, []);

  const SignatureDialog = ({ open, onClose, onSubmit }) => {
    const [name, setName] = useState('');
    const [signatureType, setSignatureType] = useState('draw'); // 'draw' or 'type'
    const [signatureData, setSignatureData] = useState('');
    const [typedSignature, setTypedSignature] = useState('');
    const sigCanvas = useRef({});
    
    const clearSignature = () => {
      if (signatureType === 'draw') {
        sigCanvas.current.clear();
        setSignatureData('');
      } else {
        setTypedSignature('');
      }
    };
    
    const handleSignatureEnd = () => {
      setSignatureData(sigCanvas.current.toDataURL());
    };
    
    const handleSubmit = () => {
      let finalSignatureData;
      if (signatureType === 'draw') {
        finalSignatureData = signatureData;
      } else {
        // Rasterize the typed signature to a data-URL image. Everything that
        // displays signatures (agreement page, PDF export) expects an image;
        // raw text here made typed e-signatures render blank in the PDF.
        const canvas = document.createElement('canvas');
        canvas.width = 500;
        canvas.height = 200;
        const ctx = canvas.getContext('2d');
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.font = '40px "Brush Script MT", cursive';
        ctx.fillStyle = 'black';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(typedSignature, canvas.width / 2, canvas.height / 2);
        finalSignatureData = canvas.toDataURL();
      }

      onSubmit({
        name,
        signatureData: finalSignatureData,
        signatureType
      });
      
      setName('');
      setTypedSignature('');
      if (signatureType === 'draw') {
        clearSignature();
      }
    };
    
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Sign Rate Agreement Approval</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By signing below, you are approving all proposed rates for this facility.
            An email will be sent to the service provider with your approval.
          </DialogContentText>
          
          <TextField
            label="Your Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            margin="normal"
            required
            helperText="This will appear as the printed name on the agreement"
          />
          
          <Box sx={{ mt: 2, mb: 2 }}>
            <FormControl component="fieldset">
              <Typography variant="subtitle2" gutterBottom>Signature Method</Typography>
              <ButtonGroup variant="outlined" size="small">
                <Button
                  variant={signatureType === 'draw' ? 'contained' : 'outlined'}
                  onClick={() => setSignatureType('draw')}
                >
                  Draw Signature
                </Button>
                <Button
                  variant={signatureType === 'type' ? 'contained' : 'outlined'}
                  onClick={() => setSignatureType('type')}
                >
                  Type Signature
                </Button>
              </ButtonGroup>
            </FormControl>
          </Box>
          
          {signatureType === 'draw' ? (
            <>
              <Box sx={{ mt: 2, border: '1px solid #ccc', borderRadius: 1 }}>
                <SignatureCanvas
                  ref={sigCanvas}
                  canvasProps={{
                    width: 500,
                    height: 200,
                    className: 'signature-canvas'
                  }}
                  onEnd={handleSignatureEnd}
                />
              </Box>
              <Button
                variant="outlined"
                size="small"
                onClick={clearSignature}
                sx={{ mt: 1 }}
              >
                Clear Signature
              </Button>
            </>
          ) : (
            <TextField
              label="Type Your Signature"
              value={typedSignature}
              onChange={(e) => setTypedSignature(e.target.value)}
              fullWidth
              margin="normal"
              required
              InputProps={{
                style: { fontFamily: 'Brush Script MT, cursive', fontSize: '24px' }
              }}
              helperText="This will be used as your electronic signature"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!name || (signatureType === 'draw' ? !signatureData : !typedSignature)}
            color="primary"
            variant="contained"
          >
            Approve & Send Confirmation
          </Button>
        </DialogActions>
      </Dialog>
    );
  };
  
  const RejectionDialog = ({ open, onClose, onSubmit, selectedRates }) => {
    const [rejectAll, setRejectAll] = useState(false);
    const [reason, setReason] = useState('');
    
    const handleSubmit = () => {
      onSubmit(rejectAll, reason);
      setRejectAll(false);
      setReason('');
    };
    
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Rate Rejection</DialogTitle>
        <DialogContent>
          <FormControlLabel
            control={
              <Checkbox
                checked={rejectAll}
                onChange={(e) => setRejectAll(e.target.checked)}
              />
            }
            label="Reject All Proposed Rates (Close Agreement)"
          />
          <TextField
            label="Rejection Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            fullWidth
            multiline
            rows={4}
            margin="normal"
            required
          />
          {!rejectAll && selectedRates.length === 0 && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              Please select at least one rate to reject or choose "Reject All"
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            disabled={!reason || (!rejectAll && selectedRates.length === 0)}
            color="warning"
            variant="contained"
          >
            Send Rejection
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  useEffect(() => {
    fetchRatesForSelection();
  }, [fetchRatesForSelection]);
  
  useEffect(() => {
    if (selectedFacilityId) {
      fetchRatesForSelection();
      fetchProposedRates(selectedFacilityId);
    }
  }, [selectedFacilityId, fetchRatesForSelection, fetchProposedRates]);
  
  // Add a useEffect to check if all decisions are complete whenever rateDecisions changes
  useEffect(() => {
    if (Object.keys(rateDecisions).length > 0) {
      
      checkAllDecisionsComplete();
    }
  }, [rateDecisions]);

  // Initial fetch and fetch on filter/pagination changes
  useEffect(() => {
    fetchFacilities(filters, facilitiesPage, facilitiesRowsPerPage);
  }, [filters, facilitiesPage, facilitiesRowsPerPage, fetchFacilities]);

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setFacilitiesPage(0); // Reset page to 0 when filters change
  };

  // Pagination Handlers for Facility List
  const handleFacilityListPageChange = (event, newPage) => {
    setFacilitiesPage(newPage);
  };

  const handleFacilityListRowsPerPageChange = (event) => {
    setFacilitiesRowsPerPage(parseInt(event.target.value, 10));
    setFacilitiesPage(0); // Reset page to 0 when rows per page changes
  };

  const handleFacilitySelect = (event) => {
    const selectedId = event.target.value;
    setSelectedFacilityId(selectedId);

    setRateAgreementStatus(null);
    setRateAgreementToken(null);
    setCurrentRateAgreement(null);
    setSubmittedRateAgreement(null);

    if (selectedId === 'DEFAULT') {
        setSelectedFacilityName('Default System Rates');
    } else if (selectedId === '') {
         setSelectedFacilityName('');
    } else {
        const facility = facilities.find(f => f._id === selectedId);
        setSelectedFacilityName(facility ? facility.facilityName : '');

        fetchRateAgreementStatus(selectedId);
        fetchCurrentRateAgreement(selectedId);
        fetchSubmittedRateAgreement(selectedId);
    }
  };

  const handleRateInputChange = (serviceTypeId, dutyLevel, field, value) => {
     const isEditingDefaults = selectedFacilityId === 'DEFAULT';
     let existingRateInfo = null;
     let rateId = null;
     let editKey = `${serviceTypeId}|${dutyLevel}`;

     if (isEditingDefaults) {
         existingRateInfo = facilityRates[serviceTypeId]?.[dutyLevel];
         rateId = existingRateInfo?._id || null;
         if (rateId) editKey = rateId;
     } else {
         const effectiveRate = Array.isArray(facilityRates) ? facilityRates.find(
             r => r.serviceType === serviceTypeId && r.dutyLevel === dutyLevel
         ) : null;
         existingRateInfo = effectiveRate;
         const rateId = effectiveRate?.isOverride ? effectiveRate?._id : null;
         if (rateId) editKey = rateId;
     }

     setRateEditState(prev => {
         const currentEdit = prev[editKey] || {};
         const initialRate = currentEdit.rate ?? existingRateInfo?.rate ?? '';
         const initialFreeMiles = currentEdit.freeMiles ?? existingRateInfo?.freeMiles ?? '';
         return {
             ...prev,
             [editKey]: {
                 rate: field === 'rate' ? value : initialRate,
                 freeMiles: field === 'freeMiles' ? value : initialFreeMiles
             }
         };
     });
  };
  
  // Removed ClosedAgreementsTab component
  const handleClearRate = async (rateId) => {
      if (!rateId || !canEditRates || selectedFacilityId === 'DEFAULT') return;
      setError(null);
      setSuccess(null);
      setIsSaving(true);
      try {
          await axios.delete(`${process.env.REACT_APP_API_URL}/api/rates/${rateId}`, { headers: getAuthHeader() });
          setSuccess('Specific rate cleared successfully. Facility will now use the system default.');
          fetchRatesForSelection();
          setRateEditState(prev => { const newState = { ...prev }; delete newState[rateId]; return newState; });
      } catch (err) {
          
          setError(err.response?.data?.message || 'Failed to clear rate.');
      } finally {
          setIsSaving(false);
      }
  };

   const handleClearDefaultRate = async (rateId) => {
    if (!rateId || !canEditRates || selectedFacilityId !== 'DEFAULT') return;
    setError(null);
    setSuccess(null);
    setIsSaving(true);
    try {
        await axios.delete(`${process.env.REACT_APP_API_URL}/api/rates/${rateId}`, { headers: getAuthHeader() });
        setSuccess('Default rate cleared successfully.');
        fetchRatesForSelection();
        setRateEditState(prev => { const newState = { ...prev }; delete newState[rateId]; return newState; });
    } catch (err) {
        
        setError(err.response?.data?.message || 'Failed to clear default rate.');
    } finally {
        setIsSaving(false);
    }
  };


  const handleSaveRates = async () => {
      const isEditingDefaults = selectedFacilityId === 'DEFAULT';
      if (!canEditRates || Object.keys(rateEditState).length === 0 || !selectedFacilityId) return;
      setError(null);
      setSuccess(null);
      setIsSaving(true);

      
      Object.entries(rateEditState).forEach(([key, values]) => {
          
      });

      const ratesToSave = [];
      Object.entries(rateEditState).forEach(([key, values]) => {
          let rateId = null;
          let serviceTypeId, dutyLevel;
          let existingRateInfo = null;

          if (key.includes('|')) {
              // serviceType|dutyLevel keys are rates with no existing row.
              // For defaults this is the normal case on a fresh database —
              // the backend upserts them when facilityId is null.
              [serviceTypeId, dutyLevel] = key.split('|');

              const editedRate = values.rate;
              const editedFreeMiles = values.freeMiles;
              if (editedRate === undefined || editedRate === '' || (isMileageService(serviceTypeId) && (editedFreeMiles === undefined || editedFreeMiles === ''))) {

                   return;
              }
          } else {
              rateId = key;
              if (isEditingDefaults) {
                  let found = false;
                  for (const sId in facilityRates) {
                      for (const dLevel in facilityRates[sId]) {
                          if (facilityRates[sId][dLevel]?._id === rateId) {
                              existingRateInfo = facilityRates[sId][dLevel];
                              serviceTypeId = sId;
                              dutyLevel = dLevel;
                              found = true;
                              break;
                          }
                      }
                      if (found) break;
                  }
              } else {
                   const effectiveRate = Array.isArray(facilityRates) ? facilityRates.find(r => r._id === rateId && r.isOverride) : null;
                   existingRateInfo = effectiveRate;
              }

              if (!existingRateInfo) {  return; }
              if (!serviceTypeId) serviceTypeId = existingRateInfo.serviceType;
              if (!dutyLevel) dutyLevel = existingRateInfo.dutyLevel;
              
          }

          if (!DUTY_LEVELS.includes(dutyLevel)) {  return; }

          ratesToSave.push({
              _id: rateId,
              facilityId: isEditingDefaults ? null : selectedFacilityId,
              serviceType: serviceTypeId,
              dutyLevel: dutyLevel,
              rate: parseFloat(values.rate) || 0,
              freeMiles: isMileageService(serviceTypeId) ? (parseFloat(values.freeMiles) || 0) : 0
          });
      });


      if (ratesToSave.length === 0) {
          setIsSaving(false);
          setError('Nothing to save — fill in a rate value (and free miles for mileage services) before saving.');
          return;
      }

      try {
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/rates`, ratesToSave, { headers: getAuthHeader() });
          if (response.status === 207) {
              setError(`Some rates failed to save: ${response.data.failedUpserts?.length || 0} errors.`);
              setSuccess(`Successfully saved ${response.data.successfulUpserts?.length || 0} rates.`);
              
          } else {
              setSuccess(`Successfully saved ${response.data.rates?.length || 0} rates.`);
          }
          setRateEditState({});
          fetchRatesForSelection();
      } catch (err) {
          
          setError(err.response?.data?.message || 'Failed to save rates.');
      } finally {
          setIsSaving(false);
      }
  };

  const handleForceResetToDefaults = async () => {
      if (!canEditRates || selectedFacilityId === 'DEFAULT' || !selectedFacilityId) return;
      setResetConfirmOpen(true);
  };

  const confirmForceResetToDefaults = async () => {
      setResetConfirmOpen(false);
      setError(null);
      setSuccess(null);
      setIsSaving(true);
      try {
          const response = await axios.delete(`${process.env.REACT_APP_API_URL}/api/rates/facility/${selectedFacilityId}`, { headers: getAuthHeader() });
          setSuccess(response.data.message || `Rates for ${selectedFacilityName} reset to default.`);
          setFacilityRates({});
          setRateEditState({});
          fetchRatesForSelection();
      } catch (err) {
          
          setError(err.response?.data?.message || 'Failed to reset rates.');
      } finally {
          setIsSaving(false);
      }
  };

  const handleCloseResetConfirm = () => {
      setResetConfirmOpen(false);
  };

  const handleApplyDefaultsPreserveExisting = async () => {
      if (!canEditRates || selectedFacilityId === 'DEFAULT' || !selectedFacilityId) return;
      setApplyConfirmOpen(true);
  };

   const confirmApplyDefaultsPreserveExisting = async () => {
      setApplyConfirmOpen(false);
      setError(null);
      setSuccess(null);
      setIsSaving(true);
      try {
          const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/rates/facility/${selectedFacilityId}/apply-defaults`, {}, { headers: getAuthHeader() });
          setSuccess(response.data.message || `Defaults applied to ${selectedFacilityName}, preserving existing rates.`);
          fetchRatesForSelection();
      } catch (err) {
          
          setError(err.response?.data?.message || 'Failed to apply defaults.');
      } finally {
          setIsSaving(false);
      }
  };
const handleCloseApplyConfirm = () => {
    setApplyConfirmOpen(false);
};

const handleGenerateRateSheet = async () => {
    if (!selectedFacilityId || selectedFacilityId === 'DEFAULT') return;
    setGeneratePdfDialogOpen(false);
    setError(null);
    setSuccess(null);
    setIsSaving(true);
    try {
        const response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/facilities/${selectedFacilityId}/generate-agreement`,
            {},
            { headers: getAuthHeader() }
        );
        
        setSuccess('Rate agreement generated and email sent successfully.');
        
        // If URL is provided, open it in a new tab
        if (response.data.agreementUrl) {
            window.open(response.data.agreementUrl, '_blank');
        } else if (response.data.token) {
            // If URL is not provided but token is, construct the URL
            const baseUrl = window.location.origin;
            window.open(`${baseUrl}/rate-agreement/${response.data.token}`, '_blank');
        }
        
        // Clear any existing proposed rates
        setProposedRates([]);
        
        // Refresh rate agreement status
        fetchRateAgreementStatus(selectedFacilityId);
        fetchCurrentRateAgreement(selectedFacilityId);
        fetchSubmittedRateAgreement(selectedFacilityId);
        
        // Force a delay before fetching proposed rates to ensure backend has time to process
        setTimeout(() => {
            
            fetchProposedRates(selectedFacilityId);
            fetchRatesForSelection();
        }, 1000);
    } catch (err) {
        
        setError(err.response?.data?.message || 'Failed to generate rate agreement.');
    } finally {
        setIsSaving(false);
    }
};

// Removed handleTabChange function

const handleAcceptAllWithSignature = () => {
  setSignatureDialogOpen(true);
};

const submitAcceptanceWithSignature = async (signatureData) => {
  if (!selectedFacilityId || !rateAgreementId) return;
  
  setSignatureDialogOpen(false);
  setIsSaving(true);
  setError(null);
  
  try {
    // Get service provider email
    const facility = facilities.find(f => f._id === selectedFacilityId);
    const serviceProviderEmail = facility?.contactEmail;
    
    const response = await axios.post(
      `${process.env.REACT_APP_API_URL}/api/facilities/${selectedFacilityId}/rate-agreement/${rateAgreementId}/accept-all-with-signature`,
      {
        adminName: signatureData.name,
        signatureData: signatureData.signatureData,
        signatureType: signatureData.signatureType || 'draw',
        serviceProviderEmail
      },
      { headers: getAuthHeader() }
    );
    
    setSuccess('All proposed rates accepted and signed. Confirmation email sent to service provider.');
    
    // Reset local state to clear the UI
    setRateDecisions({});
    setAllDecisionsComplete(false);
    setProposedRates([]);
    
    // Refresh data
    fetchRateAgreementStatus(selectedFacilityId);
    fetchCurrentRateAgreement(selectedFacilityId);
    fetchSubmittedRateAgreement(selectedFacilityId);
    fetchRatesForSelection(); // Refresh facility rates to show updated values
    
    // Force a delay before fetching proposed rates to ensure backend has time to process
    setTimeout(() => {
      
      fetchProposedRates(selectedFacilityId);
      
      // Force another refresh after a longer delay to ensure everything is updated
      setTimeout(() => {
        
        setProposedRates([]);
        fetchRateAgreementStatus(selectedFacilityId);
        fetchCurrentRateAgreement(selectedFacilityId);
      }, 2000);
    }, 1000);
  } catch (err) {
    
    setError(err.response?.data?.message || 'Failed to accept rates');
  } finally {
    setIsSaving(false);
  }
};

const handleRejectRates = () => {
  setRejectionDialogOpen(true);
};

const submitRejection = async (rejectAll, reason) => {
  if (!selectedFacilityId || !rateAgreementId) return;
  
  setRejectionDialogOpen(false);
  setIsSaving(true);
  setError(null);
  
  try {
    // Get service provider email
    const facility = facilities.find(f => f._id === selectedFacilityId);
    const serviceProviderEmail = facility?.contactEmail;
    
    if (rejectAll) {
      // Reject all rates
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/facilities/${selectedFacilityId}/rate-agreement/${rateAgreementId}/reject-all`,
        {
          rejectionReason: reason,
          serviceProviderEmail
        },
        { headers: getAuthHeader() }
      );
      
      setSuccess('All proposed rates rejected and agreement closed. Notification email sent to service provider.');
    } else {
      // Reject selected rates
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/facilities/${selectedFacilityId}/rate-agreement/${rateAgreementId}/reject-selected`,
        {
          rejections: selectedRejections,
          rejectionReason: reason,
          serviceProviderEmail
        },
        { headers: getAuthHeader() }
      );
      
      setSuccess('Selected rates rejected. Notification email sent to service provider.');
    }
    
    // Reset selected rejections
    setSelectedRejections([]);
    
    // Refresh data
    fetchRateAgreementStatus(selectedFacilityId);
    fetchCurrentRateAgreement(selectedFacilityId);
    fetchSubmittedRateAgreement(selectedFacilityId);
    fetchProposedRates(selectedFacilityId);
  } catch (err) {
    
    setError(err.response?.data?.message || 'Failed to reject rates');
  } finally {
    setIsSaving(false);
  }
};

const toggleRateRejection = (rate) => {
  const rateKey = `${rate.serviceType}|${rate.dutyLevel}`;
  const isSelected = selectedRejections.some(
    r => r.serviceType === rate.serviceType && r.dutyLevel === rate.dutyLevel
  );
  
  if (isSelected) {
    setSelectedRejections(prev => prev.filter(
      r => !(r.serviceType === rate.serviceType && r.dutyLevel === rate.dutyLevel)
    ));
  } else {
    setSelectedRejections(prev => [...prev, {
      serviceType: rate.serviceType,
      dutyLevel: rate.dutyLevel
    }]);
  }
};


  const getDisplayValue = (serviceTypeId, dutyLevel, field) => {
      const isViewingDefaults = selectedFacilityId === 'DEFAULT';
      let editKey = null;
      let rateInfo = null;

      if (isViewingDefaults) {
          rateInfo = facilityRates[serviceTypeId]?.[dutyLevel];
          const rateId = rateInfo?._id || null;
          editKey = rateId || `${serviceTypeId}|${dutyLevel}`;
      } else {
          const effectiveRate = Array.isArray(facilityRates) ? facilityRates.find(
              r => r.serviceType === serviceTypeId && r.dutyLevel === dutyLevel
          ) : null;
          rateInfo = effectiveRate;
          const rateId = effectiveRate?.isOverride ? effectiveRate?._id : null;
          editKey = rateId || `${serviceTypeId}|${dutyLevel}`;
      }

      if (rateEditState[editKey]?.[field] !== undefined) {
          return rateEditState[editKey][field];
      }

      return rateInfo?.[field] ?? '';
  };
  
  // Catalog label for a rate key (same text as every other rate surface)
  const formatServiceType = (serviceType) => rateCatalog.labelOf(serviceType);
  
  const calculateRateChange = (originalRate, proposedRate) => {
    if (originalRate === null || proposedRate === null) return '';
    
    const difference = proposedRate - originalRate;
    const percentChange = (difference / originalRate) * 100;
    
    const formattedDifference = difference >= 0 ? `+$${difference.toFixed(2)}` : `-$${Math.abs(difference).toFixed(2)}`;
    const formattedPercent = percentChange >= 0 ? `+${percentChange.toFixed(1)}%` : `${percentChange.toFixed(1)}%`;
    
    return `${formattedDifference} (${formattedPercent})`;
  };

   const hasSpecificRate = (serviceTypeId, dutyLevel) => {
       const isViewingDefaults = selectedFacilityId === 'DEFAULT';
       if (isViewingDefaults) return false;

       const effectiveRate = Array.isArray(facilityRates) ? facilityRates.find(
           r => r.serviceType === serviceTypeId && r.dutyLevel === dutyLevel
       ) : null;
       return !!effectiveRate?.isOverride;
   };

   const getRateIdForCell = (serviceTypeId, dutyLevel) => {
        const isViewingDefaults = selectedFacilityId === 'DEFAULT';
        if (isViewingDefaults) {
            return facilityRates[serviceTypeId]?.[dutyLevel]?._id || null;
        } else {
            const specificRate = Array.isArray(facilityRates) ? facilityRates.find(
               r => r.serviceType === serviceTypeId && r.dutyLevel === dutyLevel && r.isOverride
           ) : null;
           return specificRate?._id || null;
        }
   }


  const renderRateInputCell = (serviceTypeId, dutyLevel) => {
    const serviceType = ALL_SERVICE_TYPES.find(st => st.id === serviceTypeId);
    if (!serviceType || !serviceType.appliesTo.includes(dutyLevel)) {
      return <TableCell key={`${serviceTypeId}-${dutyLevel}`}></TableCell>;
    }

    const isViewingDefaults = selectedFacilityId === 'DEFAULT';
    const isSpecificOverride = !isViewingDefaults && hasSpecificRate(serviceTypeId, dutyLevel);
    const rateId = getRateIdForCell(serviceTypeId, dutyLevel);
    const rateValue = getDisplayValue(serviceTypeId, dutyLevel, 'rate');
    const freeMilesValue = getDisplayValue(serviceTypeId, dutyLevel, 'freeMiles');
    const showFreeMiles = isMileageService(serviceTypeId);
    const isGoa = serviceTypeId === 'goneOnArrival';

    const systemDefaultRate = systemDefaultRates[serviceTypeId]?.[dutyLevel]?.rate ?? 'N/A';
    const systemDefaultFreeMiles = systemDefaultRates[serviceTypeId]?.[dutyLevel]?.freeMiles ?? 'N/A';

    const isDisabled = !canEditRates || isSaving;

    const showClearButton = canEditRates && (isSpecificOverride || (isViewingDefaults && rateId));

    const editKey = rateId || `${serviceTypeId}|${dutyLevel}`;
    const isBeingEdited = rateEditState[editKey] !== undefined;

    const isZeroRateOverride = isSpecificOverride && (rateValue === '' || parseFloat(rateValue) === 0);

    if (!isViewingDefaults && isZeroRateOverride && !isBeingEdited) {
        return <TableCell key={`${serviceTypeId}-${dutyLevel}`}></TableCell>;
    }

    return (
      <TableCell key={`${serviceTypeId}-${dutyLevel}`} align="center" sx={{ verticalAlign: 'top', minWidth: showFreeMiles ? 130 : 110 }}>
        <Box display="flex" flexDirection={showFreeMiles ? "column" : "row"} alignItems="center" justifyContent="center" gap={showFreeMiles ? 1 : 0.5}>
          <TextField
            label={isGoa ? "Rate (%)" : "Rate"}
            type="text"
            InputProps={isGoa ? {
              endAdornment: <InputAdornment position="end">%</InputAdornment>,
              inputProps: { inputMode: 'numeric', pattern: '[0-9]*', min: 0, max: 100, style: { textAlign: 'right' } }
            } : {
              startAdornment: <InputAdornment position="start">$</InputAdornment>,
              inputProps: { inputMode: 'decimal', pattern: '[0-9]*[.,]?[0-9]*', step: '0.01', style: { textAlign: 'right' } }
            }}
            placeholder={String(systemDefaultRate)}
            value={rateValue}
            onChange={(e) => handleRateInputChange(serviceTypeId, dutyLevel, 'rate', e.target.value)}
            size="small"
            sx={{ width: '100px' }}
            variant="outlined"
            disabled={isDisabled}
            error={rateValue !== '' && isNaN(parseFloat(rateValue))}
          />
          {showFreeMiles && (
            <TextField
              label="Free Miles"
              type="text"
              inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min: 0, step: 1, style: { textAlign: 'right' } }}
              placeholder={String(systemDefaultFreeMiles)}
              value={freeMilesValue}
              onChange={(e) => handleRateInputChange(serviceTypeId, dutyLevel, 'freeMiles', e.target.value)}
              size="small"
              sx={{ width: '100px' }}
              variant="outlined"
              disabled={isDisabled}
              error={freeMilesValue !== '' && (isNaN(parseInt(freeMilesValue, 10)) || parseInt(freeMilesValue, 10) < 0)}
            />
          )}
          {showClearButton && (
            <Tooltip title={isViewingDefaults ? "Clear this default rate" : "Clear specific rate (revert to system default)"}>
              <span>
                <IconButton
                    size="small"
                    onClick={() => isViewingDefaults ? handleClearDefaultRate(rateId) : handleClearRate(rateId)}
                    disabled={isSaving}
                    sx={{ p: 0.5, ml: showFreeMiles ? 0 : 0.5, mt: showFreeMiles ? 0.5 : 0 }}
                    color="warning"
                >
                    <ClearIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      </TableCell>
    );
  };
  
  // Submit Decisions Dialog Component
  const SubmitDecisionsDialog = ({ open, onClose, onSubmit }) => {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
        <DialogTitle>Confirm Rate Decisions</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to submit all rate decisions? This will notify the service provider of your decisions.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            onClick={onSubmit}
            color="primary"
            variant="contained"
          >
            Submit Decisions
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const ProposedRatesSection = ({
    proposedRates,
    onAcceptAll,
    onRejectRates,
    onToggleRejection,
    selectedRejections,
    onAcceptRate,
    onRejectRate,
    allDecisionsComplete,
    onOpenSubmitDialog,
    isLoading,
    disabled
  }) => {
    // Log for debugging
    
    
    
    if (proposedRates.length === 0 && !isLoading) {
      return null;
    }
    
    return (
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Typography variant="h6">Proposed Rates from Service Provider</Typography>
          {proposedRates.length > 0 && (
            <Box>
              {}
              {allDecisionsComplete ? (
                <>
                  {}
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onOpenSubmitDialog}
                    disabled={disabled}
                  >
                    Submit All Decisions
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={onAcceptAll}
                    disabled={disabled}
                    startIcon={<CheckIcon />}
                    sx={{ mr: 1 }}
                  >
                    Accept All Proposed Rates
                  </Button>
                  <Button
                    variant="contained"
                    color="warning"
                    onClick={onRejectRates}
                    disabled={disabled}
                    startIcon={<CloseIcon />}
                  >
                    Reject Rates
                  </Button>
                </>
              )}
            </Box>
          )}
        </Box>
        
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
            <CircularProgress />
          </Box>
        ) : proposedRates.length === 0 ? (
          <Typography variant="body1" color="textSecondary">
            No proposed rates found for this facility.
          </Typography>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'grey.200' } }}>
                  <TableCell padding="checkbox">
                    <Tooltip title="Select for rejection">
                      <span>
                        <Checkbox
                          disabled={disabled}
                          indeterminate={selectedRejections.length > 0 && selectedRejections.length < proposedRates.length}
                          checked={selectedRejections.length === proposedRates.length}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedRejections(proposedRates.map(rate => ({
                                serviceType: rate.serviceType,
                                dutyLevel: rate.dutyLevel
                              })));
                            } else {
                              setSelectedRejections([]);
                            }
                          }}
                        />
                      </span>
                    </Tooltip>
                  </TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Duty Level</TableCell>
                  <TableCell align="right">Original Rate ($)</TableCell>
                  <TableCell align="right">Proposed Rate ($)</TableCell>
                  <TableCell align="right">Change</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {proposedRates.map((rate) => {
                  const isSelected = selectedRejections.some(
                    r => r.serviceType === rate.serviceType && r.dutyLevel === rate.dutyLevel
                  );
                  
                  // Get the decision for this rate (if any)
                  const rateKey = `${rate.serviceType}|${rate.dutyLevel}`;
                  const decision = rateDecisions[rateKey];
                  
                  // Check if the rate has changed
                  const hasChanged = rate.proposedRate !== rate.originalRate;
                  
                  return (
                    <TableRow
                      key={`${rate.serviceType}-${rate.dutyLevel}`}
                      hover
                      selected={isSelected}
                      sx={{
                        backgroundColor: decision === 'accepted'
                          ? 'rgba(76, 175, 80, 0.1)'
                          : decision === 'rejected'
                            ? 'rgba(244, 67, 54, 0.1)'
                            : 'inherit'
                      }}
                    >
                      <TableCell padding="checkbox">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => onToggleRejection(rate)}
                          disabled={disabled || !hasChanged}
                        />
                      </TableCell>
                      <TableCell>{formatServiceType(rate.serviceType)}</TableCell>
                      <TableCell>{DUTY_LEVEL_LABELS[rate.dutyLevel] || rate.dutyLevel}</TableCell>
                      <TableCell align="right">
                        {rate.serviceType === 'goneOnArrival'
                          ? `${rate.originalRate.toFixed(0)}%`
                          : `$${rate.originalRate.toFixed(2)}`}
                      </TableCell>
                      <TableCell align="right">
                        {rate.serviceType === 'goneOnArrival'
                          ? `${rate.proposedRate.toFixed(0)}%`
                          : `$${rate.proposedRate.toFixed(2)}`}
                      </TableCell>
                      <TableCell align="right" sx={{
                        color: rate.proposedRate > rate.originalRate ? 'error.main' : 'success.main'
                      }}>
                        {rate.serviceType === 'goneOnArrival'
                          ? `${rate.proposedRate > rate.originalRate ? '+' : ''}${(rate.proposedRate - rate.originalRate).toFixed(0)}%`
                          : calculateRateChange(rate.originalRate, rate.proposedRate)}
                      </TableCell>
                      <TableCell>
                        {hasChanged ? (
                          !decision ? (
                            <ButtonGroup size="small">
                              <Button
                                variant="outlined"
                                color="success"
                                onClick={() => onAcceptRate(rate)}
                                disabled={disabled}
                                startIcon={<CheckIcon />}
                              >
                                Accept
                              </Button>
                              <Button
                                variant="outlined"
                                color="error"
                                onClick={() => onRejectRate(rate)}
                                disabled={disabled}
                                startIcon={<CloseIcon />}
                              >
                                Reject
                              </Button>
                            </ButtonGroup>
                          ) : (
                            <Box display="flex" alignItems="center">
                              <Typography variant="body2" color={decision === 'accepted' ? 'success.main' : 'error.main'}>
                                {decision === 'accepted' ? 'Accepted' : 'Rejected'}
                              </Typography>
                              <IconButton
                                size="small"
                                onClick={() => handleUndoDecision(rate)}
                                disabled={disabled}
                                sx={{ ml: 1 }}
                              >
                                <UndoIcon fontSize="small" />
                              </IconButton>
                            </Box>
                          )
                        ) : (
                          <Typography variant="body2" color="text.secondary">
                            No change
                          </Typography>
                        )}
                      </TableCell>
                  </TableRow>
                );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    );
  };

  // Display loading spinner while user data is being fetched
  if (userLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '200px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h5" gutterBottom>Rate Management</Typography>
      {!canEditRates && access.sitesRates !== "none" && <ViewOnlyNotice>rates are shown for reference; only {orgName} regional managers and owners can change them.</ViewOnlyNotice>}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Filter Facilities</Typography>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Facility Name"
              name="name"
              value={filters.name}
              onChange={handleFilterChange}
            />
          </Grid>
          {canBrowseAllFacilities && (
            <Grid item xs={12} sm={6} md={3}>
              <TextField
                fullWidth
                size="small"
                label="Vendor #"
                name="vendorNumber"
                value={filters.vendorNumber}
                onChange={handleFilterChange}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={canBrowseAllFacilities ? 2 : 3}>
            <TextField
              fullWidth
              size="small"
              label="City"
              name="city"
              value={filters.city}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={canBrowseAllFacilities ? 1 : 2}>
            <TextField
              fullWidth
              size="small"
              label="State"
              name="state"
              value={filters.state}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="County/Parish"
              name="county"
              value={filters.county}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <TextField
              fullWidth
              size="small"
              label="Zip Code"
              name="zip"
              value={filters.zip}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Covered Zip Code"
              name="coveredZip"
              value={filters.coveredZip}
              onChange={handleFilterChange}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={1}>
            <Button
              onClick={() => fetchFacilities(filters)}
              disabled={loadingFacilities}
            >
              <SearchIcon />
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {loadingFacilities && <CircularProgress size={24} />}
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Paper sx={{ p: 2, mb: 3 }}>
          <FormControl fullWidth>
              <InputLabel id="facility-select-label">
                  {canBrowseAllFacilities ? "Select Facility or Default Rates" : "Select Facility"}
              </InputLabel>
              <Select
                  labelId="facility-select-label"
                  value={selectedFacilityId}
                  label={canBrowseAllFacilities ? "Select Facility or Default Rates" : "Select Facility"}
                  onChange={handleFacilitySelect}
                  disabled={loadingFacilities || loadingRates}
              >
                  <MenuItem value=""><em>Select...</em></MenuItem>
                  {canBrowseAllFacilities && (
                      <MenuItem value="DEFAULT"><em>Default System Rates</em></MenuItem>
                  )}
                  {filteredFacilities.map((facility) => (
                      <MenuItem key={facility._id} value={facility._id}>
                          {facility.facilityName} ({facility.vendorNumber}) - {facility.city}, {facility.state}
                      </MenuItem>
                  ))}
              </Select>
          </FormControl>
          {/* Add TablePagination for the facility list */}
          <TablePagination
            component="div"
            count={facilitiesTotalCount}
            page={facilitiesPage}
            onPageChange={handleFacilityListPageChange}
            rowsPerPage={facilitiesRowsPerPage}
            onRowsPerPageChange={handleFacilityListRowsPerPageChange}
            rowsPerPageOptions={[5, 10, 25, 50]} // Customize as needed
            disabled={loadingFacilities || loadingRates}
          />
      </Paper>


       {selectedFacilityId ? (
         <Paper sx={{ p: 2 }}>
               <Box display="flex" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" gap={1}>
                 <Typography variant="h6" gutterBottom sx={{ mb: { xs: 1, sm: 0 } }}>Rates for {selectedFacilityName}</Typography>
                 <Box display="flex" gap={1} flexWrap="wrap" alignItems="center">
                    {loadingAgreementStatus && <CircularProgress size={20} sx={{ mr: 1 }} />}

                    {canEditRates && selectedFacilityId && selectedFacilityId !== 'DEFAULT' && !loadingAgreementStatus && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => setGeneratePdfDialogOpen(true)}
                            disabled={isSaving || loadingRates}
                            size="small"
                        >
                            {rateAgreementStatus === 'Finalized' ? 'Generate New Agreement' : 'Generate Rate Agreement'}
                        </Button>
                    )}

                    {/* View buttons are available to EVERY user with access to
                        this tab (SP owners/admins included), not just Org 1
                        admins — generating/regenerating stays Org 1 only. */}
                    {selectedFacilityId !== 'DEFAULT' &&
                     ((currentRateAgreement && currentRateAgreement.token) ||
                      (rateAgreementStatus === 'Finalized' && rateAgreementToken)) && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                const baseUrl = window.location.origin;
                                const token = currentRateAgreement?.token || rateAgreementToken;
                                window.open(`${baseUrl}/rate-agreement/${token}`, '_blank');
                            }}
                            disabled={isSaving || loadingRates}
                            size="small"
                        >
                            View Rate Agreement
                        </Button>
                    )}

                    {selectedFacilityId !== 'DEFAULT' &&
                     ((submittedRateAgreement && submittedRateAgreement.token) ||
                      (rateAgreementStatus === 'Pending' && rateAgreementToken)) && (
                        <Button
                            variant="outlined"
                            color="primary"
                            onClick={() => {
                                const baseUrl = window.location.origin;
                                const token = submittedRateAgreement?.token || rateAgreementToken;
                                window.open(`${baseUrl}/rate-agreement/${token}`, '_blank');
                            }}
                            disabled={isSaving || loadingRates}
                            size="small"
                        >
                            View Rate Agreement In Negotiation
                        </Button>
                    )}

                    {/* Withdraw an agreement generated by mistake, or a negotiation both sides want to drop */}
                    {selectedFacilityId !== 'DEFAULT' && rateAgreementStatus === 'Pending' && pendingAgreementId && (
                        <CancelNegotiationButton
                            agreementId={pendingAgreementId}
                            size="small"
                            disabled={isSaving || loadingRates}
                            onCanceled={() => {
                                setProposedRates([]);
                                setRateDecisions({});
                                setSelectedRejections([]);
                                setSubmittedRateAgreement(null);
                                setPendingAgreementId(null);
                                fetchRateAgreementStatus(selectedFacilityId);
                                fetchProposedRates(selectedFacilityId);
                                setSuccess('Negotiation canceled. The other party was notified; you can generate a new rate agreement at any time.');
                            }}
                        />
                    )}

                    {canEditRates && selectedFacilityId !== 'DEFAULT' && !loadingAgreementStatus && rateAgreementStatus === 'Pending' && (
                         <Chip label="Agreement Status: Pending Provider Review" color="warning" size="small" variant="outlined" />
                    )}
                    {canEditRates && selectedFacilityId !== 'DEFAULT' && !loadingAgreementStatus && rateAgreementStatus === 'Error' && (
                         <Chip label="Error fetching agreement status" color="error" size="small" variant="outlined" />
                    )}


                    {canEditRates && selectedFacilityId !== 'DEFAULT' && (
                        <Button
                            variant="outlined"
                            color="secondary"
                            onClick={handleApplyDefaultsPreserveExisting}
                            disabled={isSaving || loadingRates}
                            startIcon={<ApplyDefaultsIcon />}
                            size="small"
                        >
                            Apply Defaults (Preserve Existing)
                        </Button>
                    )}
                    {canEditRates && selectedFacilityId !== 'DEFAULT' && (
                        <Button
                            variant="outlined"
                            color="warning"
                            onClick={handleForceResetToDefaults}
                            disabled={isSaving || loadingRates}
                            startIcon={<ResetIcon />}
                            size="small"
                        >
                            Force Reset to Default
                        </Button>
                    )}
                    {canEditRates && Object.keys(rateEditState).length > 0 && (
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveRates}
                            disabled={isSaving}
                            startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
                            size="small"
                        >
                            Save Changes
                        </Button>
                    )}
                 </Box>
               </Box>
               {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

               {loadingRates && <Box sx={{ display: 'flex', justifyContent: 'center', my: 2 }}><CircularProgress /></Box>}
               {!loadingRates && (
                   <>
                       <Typography variant="subtitle1" sx={{ mt: 2, mb: 1, fontWeight: 'bold' }}>Base Service & Mileage Rates</Typography>
                       <TableContainer component={Paper} variant="outlined" sx={{ mb: 3 }}>
                           <Table size="small" stickyHeader>
                               <TableHead>
                                   <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'grey.200' } }}>
                                       <TableCell sx={{ minWidth: 180 }}>Services</TableCell>
                                       {DUTY_LEVELS.filter(l => l !== 'additional' && l !== 'all').map(level => (
                                           <TableCell key={level} align="center" sx={{ minWidth: 130 }}>{DUTY_LEVEL_LABELS[level]}</TableCell>
                                       ))}
                                   </TableRow>
                               </TableHead>
                               <TableBody>
                                   {SERVICE_CATEGORIES.rates.map((serviceType) => (
                                       <TableRow hover key={serviceType.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                           <TableCell component="th" scope="row">{serviceType.label}</TableCell>
                                           {DUTY_LEVELS.filter(l => l !== 'additional' && l !== 'all').map(level => renderRateInputCell(serviceType.id, level))}
                                       </TableRow>
                                   ))}
                               </TableBody>
                           </Table>
                       </TableContainer>

                       <Typography variant="subtitle1" sx={{ mt: 3, mb: 1, fontWeight: 'bold' }}>Additional Charges</Typography>
                       <TableContainer component={Paper} variant="outlined">
                           <Table size="small" stickyHeader>
                               <TableHead>
                                   <TableRow sx={{ '& th': { fontWeight: 'bold', backgroundColor: 'grey.200' } }}>
                                       <TableCell sx={{ minWidth: 180 }}>Charge Type</TableCell>
                                       <TableCell align="center" sx={{ minWidth: 130 }}>Rate ($)</TableCell>
                                       <TableCell align="center" sx={{ minWidth: 130 }}>Free Miles</TableCell>
                                   </TableRow>
                               </TableHead>
                               <TableBody>
                                   {SERVICE_CATEGORIES.additionalCharges.map((serviceType) => (
                                       <TableRow hover key={serviceType.id} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                           <TableCell component="th" scope="row">{serviceType.label}</TableCell>
                                           {renderRateInputCell(serviceType.id, 'additional')}
                                       </TableRow>
                                   ))}
                               </TableBody>
                           </Table>
                       </TableContainer>

                       {/* Only show ProposedRatesSection for Org 1 admins AFTER user data has loaded */}
                       {!userLoading && canEditRates && (
                         <ProposedRatesSection
                           proposedRates={rateCatalog.sortItems(proposedRates)}
                           onAcceptAll={handleAcceptAllWithSignature}
                           onRejectRates={handleRejectRates}
                           onToggleRejection={toggleRateRejection}
                           selectedRejections={selectedRejections}
                           onAcceptRate={handleAcceptRate}
                           onRejectRate={handleRejectRate}
                           onUndoDecision={handleUndoDecision}
                           allDecisionsComplete={allDecisionsComplete}
                           onOpenSubmitDialog={handleOpenSubmitDialog}
                           isLoading={loadingProposedRates}
                           disabled={isSaving}
                         />
                       )}
                   </>
               )}
           </Paper>
       ) : (
           <Paper sx={{ p: 4, textAlign: 'center' }}>
               <Typography variant="body1" color="textSecondary">
                   {canBrowseAllFacilities ? 'Use the filters above to find and select a facility or "Default System Rates" to manage rates.' : 'Your facility rates will be displayed here once loaded.'}
               </Typography>
           </Paper>
       )}

        <Dialog
            open={resetConfirmOpen}
            onClose={handleCloseResetConfirm}
            aria-labelledby="reset-dialog-title"
            aria-describedby="reset-dialog-description"
        >
            <DialogTitle id="reset-dialog-title">{"Confirm Force Reset Rates"}</DialogTitle>
            <DialogContent>
            <DialogContentText id="reset-dialog-description">
                Are you sure you want to delete all specific rates for "{selectedFacilityName}"? The facility will revert to using system defaults. This action cannot be undone.
            </DialogContentText>
            </DialogContent>
            <DialogActions>
            <Button onClick={handleCloseResetConfirm}>Cancel</Button>
            <Button onClick={confirmForceResetToDefaults} color="warning" autoFocus>
                Force Reset
            </Button>
            </DialogActions>
        </Dialog>

         <Dialog
            open={applyConfirmOpen}
            onClose={handleCloseApplyConfirm}
            aria-labelledby="apply-dialog-title"
            aria-describedby="apply-dialog-description"
        >
            <DialogTitle id="apply-dialog-title">{"Confirm Apply Defaults"}</DialogTitle>
            <DialogContent>
            <DialogContentText id="apply-dialog-description">
                This will apply the current system default rates to any service for "{selectedFacilityName}" that currently has a rate of 0 or is not set. Existing non-zero rates will be preserved. Proceed?
            </DialogContentText>
            </DialogContent>
            <DialogActions>
            <Button onClick={handleCloseApplyConfirm}>Cancel</Button>
            <Button onClick={confirmApplyDefaultsPreserveExisting} color="primary" autoFocus>
                Apply Defaults
            </Button>
            </DialogActions>
        </Dialog>

        <Dialog
            open={generatePdfDialogOpen}
            onClose={() => setGeneratePdfDialogOpen(false)}
            aria-labelledby="generate-dialog-title"
            aria-describedby="generate-dialog-description"
        >
            <DialogTitle id="generate-dialog-title">{"Generate Rate Agreement"}</DialogTitle>
            <DialogContent>
               <DialogContentText id="generate-dialog-description">
                   This will create a rate agreement for {selectedFacilityName} and send an email with a link to the Service Provider.
                   The Service Provider can review the rates, propose changes, and electronically sign the agreement.
               </DialogContentText>
            </DialogContent>
            <DialogActions>
               <Button onClick={() => setGeneratePdfDialogOpen(false)}>Cancel</Button>
               <Button onClick={handleGenerateRateSheet} color="primary" autoFocus>
                   Generate Rate Agreement
               </Button>
            </DialogActions>
        </Dialog>

        {/* Add the dialogs */}
        <SignatureDialog
          open={signatureDialogOpen}
          onClose={() => setSignatureDialogOpen(false)}
          onSubmit={submitAcceptanceWithSignature}
        />

        <RejectionDialog
          open={rejectionDialogOpen}
          onClose={() => setRejectionDialogOpen(false)}
          onSubmit={submitRejection}
          selectedRates={selectedRejections}
        />

        <SubmitDecisionsDialog
          open={submitDialogOpen}
          onClose={() => setSubmitDialogOpen(false)}
          onSubmit={submitAllDecisions}
        />
    </Box>
 );
}

export default RateManagement;